import { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { debounce } from 'lodash'
import { usePaginatedUsers } from '../../../../../../utils/hooks/usePaginatedUsers'
import Label from '../../../../../components/_shared/Label/Label'

const NO_USER_OPTION = { label: '<No User Name>', value: '<No User Name>' }

// For incidents, user_name_tx stores the user's display name (not their ID).
const IncidentUserAsyncDropdown = ({ value, setFieldValue, showNoUser }) => {
    const [search, setSearch] = useState('')

    // Initialize with the persisted name so the selection shows on reopen.
    const [selectedValue, setSelectedValue] = useState(() => {
        if (!value) return null
        if (value === '<No User Name>') return NO_USER_OPTION
        return { label: value, value }
    })

    const { data, fetchNextPage, hasNextPage, isFetching } = usePaginatedUsers(search)

    const options = useMemo(() => {
        const users = data?.pages?.flatMap((page) => {
            const items = Array.isArray(page?.data) ? page.data : []
            return items
                .filter((u) => u.value != null && u.value !== '')
                .map((u) => ({
                    // Incidents filter by name_tx, so both value and label = name
                    label: u.label || `User ID: ${u.value}`,
                    value: u.label || `User ID: ${u.value}`,
                }))
        }) ?? []
        return showNoUser ? [NO_USER_OPTION, ...users] : users
    }, [data, showNoUser])

    const handleSearch = useMemo(
        () => debounce((inputValue) => setSearch(inputValue), 300),
        []
    )

    useEffect(() => () => handleSearch.cancel(), [handleSearch])

    // Sync when the form value changes externally (e.g. clear all).
    useEffect(() => {
        if (!value) { setSelectedValue(null); return }
        if (value === '<No User Name>') { setSelectedValue(NO_USER_OPTION); return }
        // Try to find a richer match from loaded options; fallback to the stored name.
        const match = options.find((o) => o.value === value)
        setSelectedValue(match ?? { label: value, value })
    }, [value, options])

    const handleChange = (option) => {
        setSelectedValue(option)
        // Store the display name (not user_id) — that's what the incident API filters on.
        setFieldValue('user_name_tx', option?.value ?? '')
    }

    return (
        <>
            <Label labelText="User Name" />
            <Select
                value={selectedValue || null}
                onChange={handleChange}
                onMenuOpen={() => { if (!value) setSearch('') }}
                onMenuClose={() => handleSearch.cancel()}
                options={options}
                onInputChange={(newValue, { action }) => {
                    if (action === 'input-change') handleSearch(newValue)
                    return newValue
                }}
                onMenuScrollToBottom={() => hasNextPage && fetchNextPage()}
                isLoading={isFetching}
                placeholder={isFetching ? 'Loading users…' : 'Select…'}
                filterOption={null}
                isClearable
                menuPosition="fixed"
                menuPlacement="top"
            />
        </>
    )
}

export default IncidentUserAsyncDropdown
